import { useCallback, useRef } from 'react';

/**
 * Discards the answer of a request that a newer one has already superseded.
 *
 * Effects that project a value and write it straight into a form (the termination modal fires one
 * on every date change) resolve in network order, not in the order they were issued. A slow
 * earlier response used to land on top of the newer one — and on top of a value the operator had
 * picked in the meantime, which is how a manually chosen Last Vesting Date could vanish a moment
 * after being set.
 *
 * `beginRequest()` marks its caller as the newest request and returns the predicate that caller's
 * own callback asks before writing anything:
 *
 * ```ts
 * const isCurrentRequest = beginRequest();
 * mutate(input, {
 *   onSuccess: (data) => {
 *     if (!isCurrentRequest()) return;
 *     setFormData(field, data);
 *   },
 * });
 * ```
 *
 * Calling it without issuing a request is the way to cancel a pending write — an effect that
 * bails out early still invalidates whatever is in flight.
 */
export const useRequestSequence = (): (() => () => boolean) => {
  const latestRequestId = useRef(0);

  // Stable identity: callers list this in effect dependency arrays.
  return useCallback(() => {
    const requestId = ++latestRequestId.current;
    return () => requestId === latestRequestId.current;
  }, []);
};
